/**
 * Identificador fiscal canônico do OpteFiscal 2.0.
 *
 * Fonte normativa: Plano Oficial de Construção do OpteFiscal 2.0, seção 7
 * (R-FISCAL-001) — "Nenhuma camada do OpteFiscal 2.0 poderá presumir que CNPJ
 * contém apenas dígitos."
 *
 * Regra de dígito verificador do CNPJ alfanumérico: IN RFB nº 2.229/2024 e Nota
 * Técnica do CNPJ alfanumérico. O cálculo é o mesmo módulo 11 do CNPJ numérico,
 * mas o valor de cada caractere é `código ASCII - 48` (portanto '0'..'9' valem
 * 0..9 e 'A'..'Z' valem 17..42). Os dois dígitos verificadores permanecem
 * exclusivamente numéricos.
 *
 * Consequência desejada e verificada em teste: um CNPJ numérico legado valida
 * sob exatamente a mesma regra, sem código de compatibilidade paralelo.
 *
 * Este é o ÚNICO ponto do repositório autorizado a manipular as classes de
 * caractere do identificador fiscal. Nenhum outro módulo pode reimplementar
 * normalização, máscara ou validação (ver eslint.config.js e
 * scripts/scan-cnpj-assumptions.mjs).
 */

/** Tipos de identificador fiscal suportados na fundação. */
export type FiscalTaxIdKind = 'CNPJ' | 'CPF';

/** Formato do CNPJ. Registrado no domínio para auditoria e para adapters. */
export type CnpjFormat = 'numeric' | 'alphanumeric';

declare const brand: unique symbol;

/**
 * Texto canônico validado de um identificador fiscal.
 *
 * É SEMPRE `string`. Nunca number, bigint, numeric ou coerção intermediária.
 * A marca de tipo existe para impedir que uma string arbitrária seja usada
 * onde o domínio exige um identificador já validado.
 */
export type FiscalTaxId<K extends FiscalTaxIdKind = FiscalTaxIdKind> = string & {
  readonly [brand]: K;
};

export type Cnpj = FiscalTaxId<'CNPJ'>;
export type Cpf = FiscalTaxId<'CPF'>;

export type FiscalTaxIdErrorCode =
  | 'TAX_ID_EMPTY'
  | 'TAX_ID_INVALID_CHARACTER'
  | 'TAX_ID_INVALID_LENGTH'
  | 'TAX_ID_CHECK_DIGITS_NOT_NUMERIC'
  | 'TAX_ID_CHECK_DIGITS_MISMATCH'
  | 'TAX_ID_REPEATED_CHARACTERS';

export interface FiscalTaxIdError {
  readonly ok: false;
  readonly code: FiscalTaxIdErrorCode;
  readonly message: string;
  /** Entrada normalizada até onde foi possível. Nunca contém segredo. */
  readonly input: string;
}

export interface FiscalTaxIdOk<K extends FiscalTaxIdKind> {
  readonly ok: true;
  readonly value: FiscalTaxId<K>;
  readonly kind: K;
  /** Apenas para CNPJ. CPF é sempre numérico. */
  readonly format: CnpjFormat;
}

export type FiscalTaxIdResult<K extends FiscalTaxIdKind> = FiscalTaxIdOk<K> | FiscalTaxIdError;

/**
 * Separadores de máscara aceitos por `normalizeFiscalTaxId`.
 *
 * ATENÇÃO — este conjunto é MAIS LARGO que o da norma, de propósito, porque
 * `normalizeFiscalTaxId` é auxiliar de ENTRADA (colar de planilha, digitar com
 * espaço). Quem decide o que é um CNPJ válido é `parseCnpj`, e ele usa o
 * conjunto estrito de `OFFICIAL_MASK_SEPARATORS`.
 */
const MASK_SEPARATORS = new Set([' ', '.', '-', '/', '\t', ' ']);

/**
 * Máscara aceita pela implementação de referência da Receita Federal.
 *
 * `codigos-cnpj.zip`, `src/typescript/cnpj.ts`:
 *   regexCaracteresMascara       = /[./-]/g
 *   regexCaracteresNaoPermitidos = /[^A-Z\d./-]/i
 *
 * Ponto, barra e hífen são removidos; qualquer outro caractere que não seja
 * letra ou dígito invalida a entrada — inclusive espaço e tabulação.
 */
const OFFICIAL_MASK_SEPARATORS = new Set(['.', '-', '/']);

const CNPJ_LENGTH = 14;
const CPF_LENGTH = 11;

const LOWER_A = 'a'.charCodeAt(0);
const LOWER_Z = 'z'.charCodeAt(0);

const ZERO = '0'.charCodeAt(0);
const NINE = '9'.charCodeAt(0);
const UPPER_A = 'A'.charCodeAt(0);
const UPPER_Z = 'Z'.charCodeAt(0);

function isDigit(code: number): boolean {
  return code >= ZERO && code <= NINE;
}

function isUpperLetter(code: number): boolean {
  return code >= UPPER_A && code <= UPPER_Z;
}

function isLowerLetter(code: number): boolean {
  return code >= LOWER_A && code <= LOWER_Z;
}

/**
 * Normalização ESTRITA, para validação.
 *
 * Remove somente `.`, `/` e `-`. Devolve `null` quando encontra qualquer
 * caractere que não seja letra (de qualquer caixa) ou dígito — é a
 * `regexCaracteresNaoPermitidos` da RFB, e é por isso que espaço não passa
 * aqui, embora passe em `normalizeFiscalTaxId`.
 *
 * A CAIXA É PRESERVADA de propósito. Minúscula não vira maiúscula em silêncio:
 * o vetor oficial `12.ABc.345/01DE-35` é declarado INVÁLIDO pela RFB, e o
 * `xs:pattern` do `tiposBasico_v4.00.xsd` (`[0-9A-Z]{12}[0-9]{2}`) recusaria o
 * documento no autorizador. Corrigir a caixa em silêncio transformaria uma
 * entrada inválida em NF-e — o oposto do que este domínio existe para fazer.
 */
function strictNormalize(input: string): string | null {
  let out = '';
  for (const char of input) {
    if (OFFICIAL_MASK_SEPARATORS.has(char)) continue;
    const code = char.charCodeAt(0);
    if (isDigit(code) || isUpperLetter(code) || isLowerLetter(code)) {
      out += char;
      continue;
    }
    return null;
  }
  return out;
}

/**
 * Normalização canônica.
 *
 * Remove SOMENTE separadores de máscara e aplica caixa alta. Jamais remove
 * letras — é exatamente o `replace(/\D/g, '')` que o Plano Oficial proíbe.
 */
export function normalizeFiscalTaxId(input: string): string {
  let out = '';
  for (const char of input) {
    if (MASK_SEPARATORS.has(char)) continue;
    out += char.toUpperCase();
  }
  return out;
}

/**
 * Valor do caractere para o cálculo do DV: código ASCII - 48.
 * '0'..'9' -> 0..9 ; 'A'..'Z' -> 17..42.
 */
function characterValue(char: string): number {
  return char.charCodeAt(0) - ZERO;
}

/** Módulo 11 com pesos 2..9 cíclicos da direita para a esquerda. */
function checkDigit(base: string): number {
  let sum = 0;
  let weight = 2;
  for (let i = base.length - 1; i >= 0; i--) {
    sum += characterValue(base[i] as string) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

function fail(code: FiscalTaxIdErrorCode, message: string, input: string): FiscalTaxIdError {
  return { ok: false, code, message, input };
}

function allCharactersEqual(value: string): boolean {
  const first = value[0];
  for (const char of value) {
    if (char !== first) return false;
  }
  return true;
}

/**
 * Valida e canoniza um CNPJ (numérico legado OU alfanumérico).
 *
 * Não lança. Erro é sempre explícito e classificado — o Plano Oficial proíbe
 * fallback fiscal silencioso (seção 4.4 do Script Mestre / seção 9.1 do Plano).
 */
export function parseCnpj(input: string): FiscalTaxIdResult<'CNPJ'> {
  const strict = strictNormalize(input);

  if (strict === null) {
    return fail(
      'TAX_ID_INVALID_CHARACTER',
      'CNPJ contém caractere que não é letra, dígito ou máscara oficial (. / -).',
      normalizeFiscalTaxId(input),
    );
  }

  const normalized = strict;

  if (normalized.length === 0) {
    return fail('TAX_ID_EMPTY', 'CNPJ vazio.', normalized);
  }
  if (normalized.length !== CNPJ_LENGTH) {
    return fail(
      'TAX_ID_INVALID_LENGTH',
      `CNPJ deve ter ${CNPJ_LENGTH} caracteres na forma canônica; recebido ${normalized.length}.`,
      normalized,
    );
  }

  let hasLetter = false;
  for (let i = 0; i < CNPJ_LENGTH; i++) {
    const code = (normalized[i] as string).charCodeAt(0);
    const isBaseSlot = i < 12;

    if (isBaseSlot) {
      if (isDigit(code)) continue;
      if (isUpperLetter(code)) {
        hasLetter = true;
        continue;
      }
      return fail(
        'TAX_ID_INVALID_CHARACTER',
        isLowerLetter(code)
          ? // A RFB declara `12.ABc.345/01DE-35` inválido, e o `xs:pattern` do
            // XSD da NF-e é `[0-9A-Z]{12}[0-9]{2}`. Quem tem entrada em caixa
            // baixa deve normalizar EXPLICITAMENTE, com `normalizeFiscalTaxId`.
            `Caractere minúsculo na posição ${i + 1}. O CNPJ canônico é maiúsculo; a caixa não é corrigida em silêncio.`
          : `Caractere inválido na posição ${i + 1}. A base do CNPJ aceita apenas 0-9 e A-Z.`,
        normalized,
      );
    }

    if (!isDigit(code)) {
      return fail(
        'TAX_ID_CHECK_DIGITS_NOT_NUMERIC',
        'Os dois dígitos verificadores do CNPJ são sempre numéricos.',
        normalized,
      );
    }
  }

  if (allCharactersEqual(normalized)) {
    return fail(
      'TAX_ID_REPEATED_CHARACTERS',
      'CNPJ com todos os caracteres iguais não é válido.',
      normalized,
    );
  }

  const base = normalized.slice(0, 12);
  const first = checkDigit(base);
  const second = checkDigit(base + String(first));
  const expected = `${first}${second}`;

  if (normalized.slice(12) !== expected) {
    return fail(
      'TAX_ID_CHECK_DIGITS_MISMATCH',
      'Dígitos verificadores do CNPJ não conferem.',
      normalized,
    );
  }

  return {
    ok: true,
    value: normalized as Cnpj,
    kind: 'CNPJ',
    format: hasLetter ? 'alphanumeric' : 'numeric',
  };
}

/** Valida e canoniza um CPF. CPF permanece exclusivamente numérico. */
export function parseCpf(input: string): FiscalTaxIdResult<'CPF'> {
  const normalized = normalizeFiscalTaxId(input);

  if (normalized.length === 0) {
    return fail('TAX_ID_EMPTY', 'CPF vazio.', normalized);
  }
  if (normalized.length !== CPF_LENGTH) {
    return fail(
      'TAX_ID_INVALID_LENGTH',
      `CPF deve ter ${CPF_LENGTH} dígitos; recebido ${normalized.length}.`,
      normalized,
    );
  }
  for (const char of normalized) {
    if (!isDigit(char.charCodeAt(0))) {
      return fail('TAX_ID_INVALID_CHARACTER', 'CPF aceita apenas dígitos.', normalized);
    }
  }
  if (allCharactersEqual(normalized)) {
    return fail(
      'TAX_ID_REPEATED_CHARACTERS',
      'CPF com todos os dígitos iguais não é válido.',
      normalized,
    );
  }

  const base = normalized.slice(0, 9);
  const d1 = cpfCheckDigit(base, 10);
  const d2 = cpfCheckDigit(base + String(d1), 11);
  if (normalized.slice(9) !== `${d1}${d2}`) {
    return fail(
      'TAX_ID_CHECK_DIGITS_MISMATCH',
      'Dígitos verificadores do CPF não conferem.',
      normalized,
    );
  }

  return { ok: true, value: normalized as Cpf, kind: 'CPF', format: 'numeric' };
}

function cpfCheckDigit(base: string, startWeight: number): number {
  let sum = 0;
  let weight = startWeight;
  for (const char of base) {
    sum += characterValue(char) * weight;
    weight--;
  }
  const remainder = (sum * 10) % 11;
  return remainder === 10 ? 0 : remainder;
}

/**
 * Máscara visual do CNPJ: `12.ABC.345/01DE-35`.
 *
 * A máscara NUNCA altera o valor canônico e funciona para os dois formatos.
 */
export function formatCnpj(value: Cnpj | string): string {
  const v = normalizeFiscalTaxId(String(value));
  if (v.length !== CNPJ_LENGTH) return v;
  return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12)}`;
}

/** Máscara visual do CPF: `123.456.789-09`. */
export function formatCpf(value: Cpf | string): string {
  const v = normalizeFiscalTaxId(String(value));
  if (v.length !== CPF_LENGTH) return v;
  return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
}

/** Type guard sem alocação de resultado, para uso em fronteiras de contrato. */
export function isCnpj(input: string): input is Cnpj {
  return parseCnpj(input).ok;
}

/** True quando o CNPJ canônico contém ao menos uma letra na base. */
export function isAlphanumericCnpj(value: string): boolean {
  const parsed = parseCnpj(value);
  return parsed.ok && parsed.format === 'alphanumeric';
}

/**
 * Erro lançável para fronteiras que não trabalham com Result.
 * Mantém o código de erro classificado para auditoria e resposta de API.
 */
export class FiscalTaxIdValidationError extends Error {
  readonly code: FiscalTaxIdErrorCode;
  readonly input: string;

  constructor(error: FiscalTaxIdError) {
    super(error.message);
    this.name = 'FiscalTaxIdValidationError';
    this.code = error.code;
    this.input = error.input;
  }
}

/** Versão estrita: devolve o CNPJ canônico ou lança erro classificado. */
export function assertCnpj(input: string): Cnpj {
  const parsed = parseCnpj(input);
  if (!parsed.ok) throw new FiscalTaxIdValidationError(parsed);
  return parsed.value;
}

/** Versão estrita para CPF. */
export function assertCpf(input: string): Cpf {
  const parsed = parseCpf(input);
  if (!parsed.ok) throw new FiscalTaxIdValidationError(parsed);
  return parsed.value;
}

// ---------------------------------------------------------------------------
// Raiz do CNPJ (G6A.1, §6)
// ---------------------------------------------------------------------------

/**
 * As oito primeiras posições do CNPJ — a RAIZ.
 *
 * POR QUE ISTO PRECISOU DE FUNÇÃO PRÓPRIA
 *
 *   O Manual das APIs do ADN, v1.0 (12/02/2026), introduziu uma regra nova: a
 *   consulta de distribuição aceita certificado cujo CNPJ tenha a **mesma raiz**
 *   do contribuinte consultado, e a consulta por NSU ganhou parâmetro para
 *   informar CNPJ diferente do certificado — _"é feita a validação do CNPJ Raiz
 *   nesse caso"_.
 *
 *   Ou seja: passou a existir uma comparação de identificador fiscal que não é
 *   igualdade. E comparação é exatamente onde o R-FISCAL-001 costuma ser
 *   violado — porque `raiz(a) === raiz(b)` parece inofensivo até alguém
 *   escrever `Number(cnpj.slice(0, 8))` para "normalizar".
 *
 *   Com CNPJ alfanumérico a raiz é `[0-9A-Z]{8}`. Coagir para número perde as
 *   letras e os zeros à esquerda, e faz raízes diferentes colidirem: a raiz
 *   `0012AB34` viraria `12` e casaria com `00000012`.
 *
 * A raiz é TEXTO, e sai como texto. Nunca número.
 */
export const FISCAL_TAX_ID_ROOT_LENGTH = 8;

export function fiscalTaxIdRoot(input: Cnpj | string): string {
  const normalized = normalizeFiscalTaxId(String(input));
  return normalized.slice(0, FISCAL_TAX_ID_ROOT_LENGTH);
}

/**
 * Duas inscrições pertencem ao mesmo grupo econômico?
 *
 * Comparação TEXTUAL e sensível a maiúsculas, porque a norma do CNPJ
 * alfanumérico aceita `[0-9A-Z]` e minúscula não é "quase certo" — é outro
 * identificador, ou nenhum. `normalizeFiscalTaxId` já remove pontuação e
 * espaço; ele não inventa maiúsculas onde o dado veio errado.
 */
export function sameFiscalTaxIdRoot(a: Cnpj | string, b: Cnpj | string): boolean {
  const raizA = fiscalTaxIdRoot(a);
  const raizB = fiscalTaxIdRoot(b);
  // Raiz incompleta não casa com nada: um CNPJ truncado não é um grupo.
  if (raizA.length !== FISCAL_TAX_ID_ROOT_LENGTH) return false;
  if (raizB.length !== FISCAL_TAX_ID_ROOT_LENGTH) return false;
  return raizA === raizB;
}
